import { EventEmitter } from 'events';
import IPostMessage from '../IPostMessage';
import validate from '../Validate/Validate';
import IDebug, { IDebugSetWeinreMessage, WeinreData } from './IDebug';

/**
 * The `sos.debug` API groups together methods for checking the state of remote debug mode.
 *
 * :::note
 * - This debug is not the "native debug" of the device, but rather a remote debug mode that allows you to connect to the device using Weinre.
 * - State of the remote debug mode or native debug mode can be changed via Cloud Control or [Rest API](https://developers-staging.signageos.io/api/#tag/DeviceDebug/paths/~1v1~1device~1%7BdeviceUid%7D~1debug/put).
 * :::
 */
export default class Debug extends EventEmitter implements IDebug {
	public static MESSAGE_PREFIX: string = 'debug';
	public static DEBUG_CHANGED: string = 'debug.changed';

	/** @internal */
	constructor(
		public readonly window: Window & WeinreData,
		private messagePrefix: string,
		private postMessage: IPostMessage<any>,
	) {
		super();
	}

	/**
	 * The `isRemoteDebugEnabled()` method returns the state of the Device debug mode.
	 *
	 * @returns {Promise<boolean>} Resolves to `true` if the remote debug mode is enabled, otherwise `false`.
	 * @since 5.8.0
	 */
	public async isRemoteDebugEnabled(): Promise<boolean> {
		const { isEnabled } = await this.postMessage({
			type: this.getMessage('is_remote_enabled'),
		});

		return isEnabled;
	}

	/**
	 * The `onRemoteDebugChanged()` method sets up a listener, which is called whenever the state of the Device debug mode is changed.
	 *
	 * @param listener The listener to be called when the state of the remote debug mode is changed.
	 * @returns {void} Resolves when the listener is successfully set up.
	 * @since 5.8.0
	 */
	public onRemoteDebugChanged(listener: (data: { enabled: boolean }) => void): void {
		validate({ listener }).required().function();
		this.addListener(Debug.DEBUG_CHANGED, (data) => listener({ enabled: data.enabled }));
	}

	/** @internal */
	public handleMessageData(data: IDebugSetWeinreMessage) {
		switch (data.type) {
			case this.getMessage('configure'):
				this.emit(Debug.DEBUG_CHANGED, {
					enabled: data.enabled,
				});
				if (data.enabled) {
					this.window.WeinreServerURL = data.url;
					this.window.WeinreServerId = data.id;
					if (!this.window.__weinreLoaded) {
						const script = this.window.document.createElement('script');
						script.src = data.url + '/target/target-script-min.js';
						this.window.document.body.appendChild(script);

						this.window.__weinreLoaded = true;
					}
				} else {
					this.window.WeinreServerURL = undefined;
					this.window.WeinreServerId = undefined;
				}
				break;
			default:
		}
	}

	private getMessage(name: string) {
		return this.messagePrefix + '.' + Debug.MESSAGE_PREFIX + '.' + name;
	}
}
